import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BookOpenText, Loader2, RefreshCw, X, AlertTriangle } from "lucide-react";

const List = ({ items }) => !items?.length ? <span className="text-slate-300">None documented</span> : (
  <ul className="space-y-0.5">{items.map((t, i) => <li key={i} className="flex gap-1.5"><span className="text-slate-300">•</span><span>{t}</span></li>)}</ul>
);

export const AiCaseDigest = ({ caseId, caseNumber, onClose }) => {
  const [data, setData] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/ai/case-digest/${caseId}`).then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try { await api.post(`/ai/case-digest/${caseId}/generate`); toast.success("Digest generated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Digest generation failed"); }
    finally { setBusy(false); }
  };

  const d = data?.digest;
  const c = d?.content || {};
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="ai-case-digest">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-navy flex items-center gap-2"><BookOpenText className="w-5 h-5 text-indigo-500" /> Medical Intelligence Digest — {caseNumber}</h3>
          <button onClick={onClose} data-testid="digest-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {data && <p className="mt-2 text-[11px] font-semibold text-indigo-700 bg-indigo-100/70 rounded-xl px-3 py-2" data-testid="digest-disclaimer">{data.disclaimer}</p>}

        {data === undefined ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
          data === null ? <p className="mt-4 text-sm text-red-500">Could not load the digest.</p> : (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={generate} disabled={busy || !data.summaries_available} data-testid="digest-generate"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Merging charts…" : d ? "Regenerate digest" : "Generate digest"}
              </button>
              <span className="text-[11px] text-slate-400">{data.summaries_available} chart summar{data.summaries_available === 1 ? "y" : "ies"} available on this case</span>
              {data.is_stale && (
                <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-600 bg-amber-50 rounded-full px-3 py-1" data-testid="digest-stale">
                  <AlertTriangle className="w-3 h-3" /> New charts since this digest — regenerate for the latest picture
                </span>
              )}
            </div>

            {!d && <p className="mt-5 text-sm text-slate-400" data-testid="digest-empty">{data.summaries_available ? "No digest yet — generate one to merge every chart into a single chronological narrative." : "No AI chart summaries on this case yet. Generate chart summaries first."}</p>}

            {d?.status === "ready" && (
              <div className="mt-5 space-y-4 text-sm text-slate-600" data-testid="digest-content">
                <div>
                  <div className="font-bold text-navy">Treatment Narrative</div>
                  <p className="mt-1 whitespace-pre-line">{c.narrative}</p>
                  <div className="text-[10px] text-slate-400 mt-1">{d.charts_included} chart(s) merged · {new Date(d.generated_at).toLocaleString()} · {d.model}</div>
                </div>
                {(c.chronology || []).length > 0 && (
                  <div data-testid="digest-chronology">
                    <div className="font-bold text-navy mb-1">Chronology</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                          <th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Provider</th><th className="px-2 py-1.5">Visit</th><th className="px-2 py-1.5">Key findings</th></tr></thead>
                        <tbody>
                          {c.chronology.map((row, i) => (
                            <tr key={i} className="border-b border-slate-50 last:border-0 align-top">
                              <td className="px-2 py-1.5 whitespace-nowrap">{row.date || "—"}</td>
                              <td className="px-2 py-1.5">{row.provider}</td>
                              <td className="px-2 py-1.5">{(row.visit_type || "").replace(/_/g, " ")}</td>
                              <td className="px-2 py-1.5"><List items={row.key_findings} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><div className="font-bold text-navy">Overall injury summary</div><p className="mt-0.5">{c.overall_injury_summary || "—"}</p></div>
                  <div><div className="font-bold text-navy">Treatment progression</div><p className="mt-0.5">{c.treatment_progression || "—"}</p></div>
                  <div><div className="font-bold text-navy">Outstanding care</div><div className="mt-0.5"><List items={c.outstanding_care} /></div></div>
                  <div><div className="font-bold text-navy">Missing records to obtain</div><div className="mt-0.5"><List items={c.missing_records} /></div></div>
                  <div><div className="font-bold text-navy">Documentation strength notes</div><div className="mt-0.5"><List items={c.documentation_strength_notes} /></div></div>
                  <div><div className="font-bold text-navy text-red-600">Red flags</div><div className="mt-0.5"><List items={c.red_flags} /></div></div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AiCaseDigest;
